/**
 * Authoring helpers.
 *
 * Lessons are plain data (§24), but plain data is verbose to write by hand.
 * These builders keep a lesson file readable as *prose about teaching* rather
 * than as a wall of object literals — while still producing exactly the
 * `Lesson` shape the renderer and the validator expect.
 */

import type { Block, Figure, Lesson, LessonSection, Step } from '@yuan-math/curriculum';

export const text = (value: string): Block => ({ type: 'text', value });

export const math = (latex: string, caption?: string): Block =>
  caption === undefined ? { type: 'math', latex } : { type: 'math', latex, caption };

export const steps = (items: Step[]): Block => ({ type: 'steps', steps: items });

export const list = (items: string[], ordered = false): Block => ({ type: 'list', ordered, items });

export const insight = (value: string, title?: string): Block =>
  title === undefined ? { type: 'callout', tone: 'insight', value } : { type: 'callout', tone: 'insight', title, value };

export const warning = (value: string, title?: string): Block =>
  title === undefined ? { type: 'callout', tone: 'warning', value } : { type: 'callout', tone: 'warning', title, value };

export const tip = (value: string, title?: string): Block =>
  title === undefined ? { type: 'callout', tone: 'tip', value } : { type: 'callout', tone: 'tip', title, value };

export const figure = (fig: Figure, caption?: string): Block =>
  caption === undefined ? { type: 'figure', figure: fig } : { type: 'figure', figure: fig, caption };

export const table = (headers: string[], rows: string[][]): Block => ({ type: 'table', headers, rows });

export const practice = (conceptId: string, count: number, prompt?: string): Block =>
  prompt === undefined ? { type: 'practice', conceptId, count } : { type: 'practice', conceptId, count, prompt };

type SectionInput = Partial<Record<LessonSection['kind'], { title: string; blocks: Block[] }>>;

/**
 * Builds a lesson, emitting sections in the canonical order.
 *
 * The order is fixed on purpose: every lesson in the platform walks the same
 * arc, so a student always knows where they are and what comes next.
 */
export function lesson(config: {
  id: string;
  unitId: string;
  conceptId: string;
  title: string;
  summary: string;
  objectives: string[];
  estimatedMinutes: number;
  sections: SectionInput;
}): Lesson {
  const ORDER: LessonSection['kind'][] = [
    'introduction',
    'intuition',
    'visual',
    'worked-example',
    'guided',
    'independent',
    'challenge',
    'review',
  ];

  const sections: LessonSection[] = ORDER.flatMap((kind) => {
    const section = config.sections[kind];
    return section ? [{ kind, title: section.title, blocks: section.blocks }] : [];
  });

  return {
    id: config.id,
    unitId: config.unitId,
    conceptId: config.conceptId,
    title: config.title,
    summary: config.summary,
    objectives: config.objectives,
    estimatedMinutes: config.estimatedMinutes,
    sections,
  };
}
